// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component, Prop, Vue } from 'vue-property-decorator';
let Countdown = class Countdown extends Vue {
    constructor() {
        super(...arguments);
        /**
         * Days left until target date
         */
        this.days = 0;
        /**
         * Hours left until target date
         */
        this.hours = 0;
        /**
         * Minutes left until target date
         */
        this.minutes = 0;
        /**
         * Flag to track if countdown is still active
         * Reactivity fix after countdown is finished
         */
        this.isActive = true;
        /**
         * Timer interval reference
         */
        this.timer = null;
        /**
         * Timeout reference for initial sync
         */
        this.initialTimeout = null;
    }
    created() {
        this.updateCountdown();
    }
    /**
     * Component lifecycle hook - mounted (runs during client-side render)
     */
    mounted() {
        this.isActive = true;
        if (typeof window !== 'undefined' && this.showCountdown) {
            this.startSynchronizedInterval();
        }
    }
    /**
     * Component lifecycle hook - called before component is destroyed
     */
    beforeDestroy() {
        this.clearTimers();
    }
    /**
     * Get the box style with configured background color
     */
    get boxStyle() {
        if (!this.boxColor) {
            return {};
        }
        return {
            backgroundColor: this.boxColor
        };
    }
    /**
     * Get the text style with configured text color
     */
    get textStyle() {
        if (!this.textColor) {
            return {};
        }
        return {
            color: this.textColor
        };
    }
    /**
     * Check if countdown prefix is set
     */
    get hasPrefix() {
        return !!this.prefix;
    }
    /**
     * Format number to display at least two digits (e.g. "05" instead of "5")
     */
    formatNumber(num) {
        return num < 10 ? `0${num}` : `${num}`;
    }
    /**
     * Returns an array of countdown items with their values and labels.
     * Each item contains a formatted value and a corresponding time unit label.
     * Days are only included if greater than 0.
     */
    get items() {
        const items = [];
        const daysLabel = this.$t('modules.headingCountdown.countdown.days');
        const hoursLabel = this.$t('modules.headingCountdown.countdown.hours');
        const minutesLabel = this.$t('modules.headingCountdown.countdown.minutes');
        if (this.days > 0) {
            items.push({ value: `${this.days}`, label: daysLabel });
        }
        items.push({ value: this.formatNumber(this.hours), label: hoursLabel });
        items.push({ value: this.formatNumber(this.minutes), label: minutesLabel });
        return items;
    }
    /**
     * Calculate and update the countdown values
     */
    updateCountdown() {
        if (!this.target) {
            return;
        }
        const now = new Date();
        const target = new Date(this.target);
        const diff = target.getTime() - now.getTime();
        if (diff <= 0) {
            this.days = 0;
            this.hours = 0;
            this.minutes = 0;
            this.isActive = false;
            this.clearTimers();
            return;
        }
        this.days = Math.floor(diff / (1000 * 60 * 60 * 24));
        this.hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        this.minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    }
    /**
     * Check if countdown should be shown
     */
    get showCountdown() {
        if (!this.target || !this.isActive) {
            return false;
        }
        const now = new Date();
        const target = new Date(this.target);
        // Hide countdown if target date has passed
        return target.getTime() > now.getTime();
    }
    /**
     * Clear all timers (interval and timeout)
     */
    clearTimers() {
        if (this.timer && typeof window !== 'undefined') {
            window.clearInterval(this.timer);
            this.timer = null;
        }
        if (this.initialTimeout && typeof window !== 'undefined') {
            window.clearTimeout(this.initialTimeout);
            this.initialTimeout = null;
        }
    }
    /**
     * Starts a synchronized interval for countdown updates that aligns with full minutes.
     *
     * This method ensures that the countdown updates occur exactly at the start of each minute.
     * It works by:
     * 1. Calculating the time remaining until the next full minute
     * 2. Performing an immediate countdown update
     * 3. Setting a timeout to trigger at the next full minute
     * 4. Starting a regular interval that runs every minute thereafter
     *
     * This approach ensures precise minute-based updates and prevents drift over time.
     */
    startSynchronizedInterval() {
        // Calculate milliseconds until next full minute
        const now = new Date();
        const secondsToNextMinute = 60 - now.getSeconds();
        const msToNextMinute = secondsToNextMinute * 1000 - now.getMilliseconds();
        // First update countdown immediately
        this.updateCountdown();
        // Set timeout to sync with next full minute
        this.initialTimeout = window.setTimeout(() => {
            // Update countdown at the full minute
            this.updateCountdown();
            // Start interval that runs exactly on full minutes
            this.timer = window.setInterval(() => {
                this.updateCountdown();
            }, 60 * 1000);
            this.initialTimeout = null;
        }, msToNextMinute);
    }
};
__decorate([
    Prop({ type: String, required: true })
], Countdown.prototype, "target", void 0);
__decorate([
    Prop({ type: String, required: false })
], Countdown.prototype, "prefix", void 0);
__decorate([
    Prop({ type: String, required: false })
], Countdown.prototype, "boxColor", void 0);
__decorate([
    Prop({ type: String, required: false })
], Countdown.prototype, "textColor", void 0);
Countdown = __decorate([
    Component({
        name: 'Countdown',
        components: {},
        template: `
    <div class="Countdown">
      <span v-if="hasPrefix" class="Countdown__prefix">
        {{ prefix }}
      </span>

      <div v-for="(item, index) in items" :key="index" class="Countdown__group">
        <span class="Countdown__value__box" :style="boxStyle">
          <span class="Countdown__value" :style="textStyle">
            {{ item.value }}
          </span>
        </span>
        <span class="Countdown__label">{{ item.label }}</span>
      </div>
    </div>
  `
    })
], Countdown);
export { Countdown };
